import React, { useState, useEffect, useRef } from 'react';
import { 
  Container, 
  Nav, 
  Navbar, 
  Row, 
  Col,
  Dropdown,
  Badge,
  Button
} from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import axiosInstance from '../../helpers/api';
import toast from 'react-hot-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMap,
  faComments,
  faHeart,
  faCalendarCheck,
  faChartLine,
  faCog,
  faSignOutAlt,
  faQuestionCircle,
  faPhone,
  faUser,
  faEnvelope,
  faSearch,
  faTimes,
  faFileContract
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import MapView from './MapView';
import CustomerPortalChat from './CustomerPortalChat';
import ActivityDashboard from './ActivityDashboard';
import Preferences from './Preferences';
import AgentMessageModal from './AgentMessageModal';
import SavedSearches from './SavedSearches';
import SavedSearchesPanel from './SavedSearchesPanel';
import ToursPanel from './ToursPanel';
import CustomerDocuments from './CustomerDocuments';
import './CustomerPortal.css';

interface UserSession {
  id: string;
  email: string;
  name: string;
  agentId: string;
  agentName: string;
  agentPhone: string;
  agentEmail?: string;
  agentPhoto?: string;
  dealId: string;
  preferences?: any;
  isPublicUser?: boolean;
}

type ViewType = 'map' | 'chat' | 'saved' | 'tours' | 'searches' | 'activity' | 'preferences' | 'documents';

const CustomerPortal: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useParams<{ token?: string }>();
  const initialQuery = location.state?.initialQuery;
  
  const [user, setUser] = useState<UserSession | null>(null);
  const isAuthenticated = !!user && !user.email.includes('guest@');
  const [currentView, setCurrentView] = useState<ViewType>('map');
  const [properties, setProperties] = useState<any[]>([]);
  const [savedProperties, setSavedProperties] = useState<any[]>([]);
  const [savedPropertyIds, setSavedPropertyIds] = useState<Set<string>>(new Set());
  const [tours, setTours] = useState<any[]>([]);
  const [tourCount, setTourCount] = useState<number>(() => {
    // Get tour count from localStorage
    const saved = localStorage.getItem('tourProperties');
    return saved ? JSON.parse(saved).length : 0;
  });
  
  // Get tour properties based on saved IDs
  const getTourProperties = () => {
    const savedTourIds = localStorage.getItem('tourProperties');
    const tourPropertyIds = savedTourIds ? JSON.parse(savedTourIds) : [];
    return properties.filter(p => tourPropertyIds.includes(p.mlsNumber));
  };
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showAgentMessage, setShowAgentMessage] = useState(false);
  const [agentMessage, setAgentMessage] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchFilters, setSearchFilters] = useState<any>({});
  const [showSavedSearchPanel, setShowSavedSearchPanel] = useState(false);
  const [showToursPanel, setShowToursPanel] = useState(false);
  const [pendingDocuments, setPendingDocuments] = useState(0);
  const urlProcessedRef = useRef(false);
  
  // Seller-specific state
  const [sellerProperty, setSellerProperty] = useState<any>(null);
  const [showSellerOverlay, setShowSellerOverlay] = useState(false);

  useEffect(() => {
    // Check authentication
    checkAuth();
  }, [token]);

  // Listen for tour updates
  useEffect(() => {
    const handleStorageChange = () => {
      const saved = localStorage.getItem('tourProperties');
      setTourCount(saved ? JSON.parse(saved).length : 0);
    };
    
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('tourUpdated', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('tourUpdated', handleStorageChange);
    };
  }, []);

  // Fetch seller property valuation when address is set
  useEffect(() => {
    if (sellerProperty?.address && !sellerProperty.estimatedValue) {
      fetchSellerPropertyValuation(sellerProperty.address);
    }
  }, [sellerProperty?.address]);

  useEffect(() => {
    // Load properties once authentication check is complete
    if (!loading) {
      loadProperties();
      fetchPendingDocuments();
      loadSavedProperties();
      
      // Check URL parameters first
      const urlParams = new URLSearchParams(window.location.search);
      const hasUrlParams = urlParams.toString() !== '';
      
      if (hasUrlParams && !urlProcessedRef.current) {
        urlProcessedRef.current = true;
        // Process URL parameters
        console.log('🔗 Processing URL parameters:', urlParams.toString());
        
        const filters: any = {};
        const query = urlParams.get('q') || '';
        const sellerAddress = urlParams.get('address'); // Check for seller address
        const leadId = urlParams.get('lid');
        const agentId = urlParams.get('aid');
        
        // Check if this is a seller viewing their property
        if (sellerAddress) {
          console.log('🏡 Seller property detected:', sellerAddress);
          setShowSellerOverlay(true);
          setSellerProperty({
            address: sellerAddress,
            leadId,
            agentId
          });
          
          // Process the seller's address to center map and show their property
          handleInitialQuery(sellerAddress);
          
          // Also set the address in filters for context
          filters.sellerAddress = sellerAddress;
        } else {
          // Regular buyer search
          // Extract search criteria from URL
          if (urlParams.get('city')) filters.city = urlParams.get('city');
          if (urlParams.get('zip')) filters.zipCode = urlParams.get('zip');
          if (urlParams.get('minPrice')) filters.minPrice = parseInt(urlParams.get('minPrice') || '0');
          if (urlParams.get('maxPrice')) filters.maxPrice = parseInt(urlParams.get('maxPrice') || '0');
          if (urlParams.get('beds')) filters.bedrooms = parseInt(urlParams.get('beds') || '0');
          if (urlParams.get('baths')) filters.bathrooms = parseInt(urlParams.get('baths') || '0');
          if (urlParams.get('type')) filters.propertyType = urlParams.get('type');
          if (urlParams.get('pool') === 'true') filters.pool = true;
          if (urlParams.get('features')) filters.features = urlParams.get('features')?.split(',');
          if (urlParams.get('minSqft')) filters.sqftMin = parseInt(urlParams.get('minSqft') || '0');
          if (urlParams.get('maxSqft')) filters.sqftMax = parseInt(urlParams.get('maxSqft') || '0');
          if (urlParams.get('garage')) filters.garage = parseInt(urlParams.get('garage') || '0');
          if (urlParams.get('yearMin')) filters.yearBuiltMin = parseInt(urlParams.get('yearMin') || '0');
          if (urlParams.get('yearMax')) filters.yearBuiltMax = parseInt(urlParams.get('yearMax') || '0');
          if (urlParams.get('lotMin')) filters.lotSizeMin = parseInt(urlParams.get('lotMin') || '0');
          if (urlParams.get('hoaMax')) filters.hoaMax = parseInt(urlParams.get('hoaMax') || '0');
          if (urlParams.get('waterfront') === 'true') filters.waterfront = true;
          if (urlParams.get('golf') === 'true') filters.golfCourse = true;
          if (urlParams.get('new') === 'true') filters.newConstruction = true;
          if (urlParams.get('reduced') === 'true') filters.priceReduction = true;
          
          setSearchQuery(query);
          
          // Process with Theta AI if we have a query, otherwise load properties with filters
          if (query) {
            handleInitialQuery(query);
          } else {
            loadPropertiesWithFilters(filters);
          }
        }
        
        setSearchFilters(filters);
        
        // Show map view
        setCurrentView('map');
      } else {
        // Check for Theta AI response (from Hero section)
        const thetaAIResponseStr = sessionStorage.getItem('thetaAIResponse');
        const searchQuery = sessionStorage.getItem('searchQuery');
        
        if (thetaAIResponseStr && searchQuery) {
          try {
            const thetaAIResponse = JSON.parse(thetaAIResponseStr);
            console.log('🤖 Using cached Theta AI response from Hero section');
            
            // Update properties with cached Theta AI results
            if (thetaAIResponse.properties && thetaAIResponse.properties.length > 0) {
              setProperties(thetaAIResponse.properties);
              setSearchQuery(searchQuery);
              
              // Update search filters from parsed criteria
              if (thetaAIResponse.criteria) {
                setSearchFilters(thetaAIResponse.criteria);
              }
              
              // Show AI response in console
              if (thetaAIResponse.aiResponse) {
                console.log('💬 Theta AI says:', thetaAIResponse.aiResponse);
              }
              
              // Show map view
              setCurrentView('map');
            }
            
            // Clear the cached response
            sessionStorage.removeItem('thetaAIResponse');
            sessionStorage.removeItem('searchQuery');
          } catch (error) {
            console.error('Error parsing cached Theta AI response:', error);
            // Fall back to regular query processing
            if (searchQuery) {
              handleInitialQuery(searchQuery);
            }
          }
        } else {
          // Check for query from localStorage (fallback)
          const storedQuery = localStorage.getItem('chatRealEstateQuery');
          if (storedQuery) {
            handleInitialQuery(storedQuery);
            // Clear the stored query
            localStorage.removeItem('chatRealEstateQuery');
          } else if (initialQuery) {
            // If we have an initial query from navigation state, process it
            handleInitialQuery(initialQuery);
          }
        }
      }
    }
  }, [loading]);

  const checkAuth = async () => {
    try {
      // Check if user is on Chat.RealEstate domain - they can browse as guests
      // Check if this is a Chat.RealEstate domain or localhost with portal parameter
      const urlParams = new URLSearchParams(window.location.search);
      const isChatRealEstateDomain = window.location.hostname.includes('chat.realestate') ||
                                     window.location.hostname.includes('kairos.realestate') ||
                                     (window.location.hostname === 'localhost' && urlParams.get('portal') === 'chat') ||
                                     window.location.hostname === 'local.chat.realestate';

      // If coming from invite token, redirect to signup
      if (token) {
        navigate(`/customer-portal/signup/${token}`);
        return;
      } else {
        // Check existing session - support both customer portal and public portal tokens
        const customerPortalToken = localStorage.getItem('customerPortalToken');
        const publicPortalToken = localStorage.getItem('auth_token');
        const authToken = customerPortalToken || publicPortalToken;
        
        if (!authToken && !isChatRealEstateDomain) {
          // Only redirect to sign-in if NOT on chat.realestate domain
          navigate('/sign-in');
          return;
        } else if (!authToken && isChatRealEstateDomain) {
          // Create guest user for Chat.RealEstate visitors
          setUser({
            id: 'guest-' + Date.now(),
            email: 'guest@chat.realestate',
            name: 'Guest User',
            agentId: 'guest',
            agentName: 'KairosIQ AI Realty Agent',
            agentPhone: '1-888-KAIROS',
            dealId: 'guest',
            preferences: {}
          });
        } else {
          // Check if this is a public portal user
          if (publicPortalToken && !customerPortalToken) {
            // For public portal users, use stored user info
            const storedUserInfo = localStorage.getItem('user_info');
            if (storedUserInfo) {
              try {
                const userData = JSON.parse(storedUserInfo);
                setUser({
                  id: userData.id || userData.user_id,
                  email: userData.email || userData.user_email,
                  name: `${userData.firstName || userData.user_first_name || ''} ${userData.lastName || userData.user_last_name || ''}`.trim() || 'User',
                  agentId: 'public-portal',
                  agentName: 'KairosIQ AI Realty',
                  agentPhone: '1-888-KAIROS',
                  dealId: 'public',
                  preferences: {},
                  isPublicUser: true
                });
              } catch (parseError) {
                console.error('Failed to parse user info:', parseError);
                navigate('/sign-in');
              }
            } else {
              navigate('/sign-in');
            }
          } else if (authToken) {
            // Validate token - try customer portal first, then regular auth
            try {
              let userInfo = null;
              
              // First try customer portal endpoint
              if (customerPortalToken) {
                try {
                  const response = await axiosInstance.get('/customer-portal/me', {
                    headers: { 'Authorization': `Bearer ${authToken}` }
                  });
                  if (response.data.success) {
                    userInfo = response.data.user;
                  }
                } catch (error) {
                  console.log('Customer portal auth failed, trying regular auth...');
                }
              }
              
              // If customer portal failed or we have public portal token, try regular auth
              if (!userInfo && publicPortalToken) {
                try {
                  const response = await axiosInstance.get('/auth/me', {
                    headers: { 'Authorization': `Bearer ${authToken}` }
                  });
                  if (response.data.success) {
                    // Convert regular user to customer portal format
                    const userData = response.data.data;
                    userInfo = {
                      id: userData.id,
                      email: userData.user_email || userData.email,
                      name: `${userData.user_first_name || ''} ${userData.user_last_name || ''}`.trim() || userData.name || 'User',
                      agentId: userData.assigned_agent_id || 'unassigned',
                      agentName: 'Chat.RealEstate Support',
                      agentPhone: '1-800-REALTOR',
                      dealId: userData.deal_id || 'general',
                      preferences: userData.preferences || {},
                      isPublicUser: true
                    };
                  }
                } catch (error) {
                  console.error('Regular auth validation failed:', error);
                }
              }
              
              if (userInfo) {
                setUser(userInfo);
              } else {
                navigate('/sign-in');
              }
            } catch (error) {
              console.error('Token validation failed:', error);
              navigate('/sign-in');
            }
          } else {
            // Check if coming from chat.realestate

            const fromChatRealEstate = sessionStorage.getItem('fromChatRealEstate') === 'true';

            // No valid token
            if (!fromChatRealEstate) {
              navigate('/sign-in');
            } else {
              // Create guest user for Chat.RealEstate visitors
              setUser({
                id: 'guest-' + Date.now(),
                email: 'guest@chat.realestate',
                name: 'Guest User',
                agentId: 'guest',
                agentName: 'KairosIQ AI Realty Agent',
                agentPhone: '1-888-KAIROS',
                dealId: 'guest',
                preferences: {}
              });
            }
          }
        }
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      // Check if this is a Chat.RealEstate domain or localhost with portal parameter
      const urlParams = new URLSearchParams(window.location.search);
      const isChatRealEstateDomain = window.location.hostname.includes('chat.realestate') ||
                                     window.location.hostname.includes('kairos.realestate') ||
                                     (window.location.hostname === 'localhost' && urlParams.get('portal') === 'chat') ||
                                     window.location.hostname === 'local.chat.realestate';
      if (!isChatRealEstateDomain) {
        navigate('/sign-in');
      } else {
        // Create guest user for error case
        setUser({
          id: 'guest-' + Date.now(),
          email: 'guest@chat.realestate',
          name: 'Guest User',
          agentId: 'guest',
          agentName: 'KairosIQ AI Assistant',
          agentPhone: '1-888-KAIROS',
          dealId: 'guest',
          preferences: {}
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const loadProperties = async () => {
    console.log('🏠 Loading properties...');
    try {
      const authToken = localStorage.getItem('customerPortalToken');
      const headers: any = {
        'Content-Type': 'application/json'
      };
      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      }

      // Check if we have existing search filters to preserve
      const hasActiveFilters = Object.keys(searchFilters).length > 0;
      
      // Build params from existing filters or defaults
      let params: any = {
        limit: 2000,
        state: 'AZ'
      };

      if (hasActiveFilters) {
        // Use the current search filters
        params = {
          ...params,
          ...searchFilters
        };
      } else {
        // Only use defaults if no search has been performed
        const defaultParams = user?.preferences || {
          city: 'Phoenix',
          minPrice: 400000,
          maxPrice: 700000
        };
        params = {
          ...params,
          ...defaultParams
        };
      }

      // Using map endpoint to get REAL MLS properties
      const response = await axiosInstance.get('/mls-portal/properties/map', {
        params,
        headers
      });
      
      const data = response.data;
      console.log('📡 API Response:', data);
      
      if (data.success && data.data) {
        console.log('✅ Found', data.data.length, 'properties');
        // Transform properties to ensure proper format for map
        const transformedProperties = data.data.map((property: any) => {
          // Properties already come in the correct format from our API
          return {
            mlsNumber: property.mlsNumber || property.id,
            price: property.price || 0,
            bedrooms: property.bedrooms || 0,
            bathrooms: property.bathrooms || 0,
            sqft: property.sqft || 0,
            address: property.address || {
              street: '',
              city: '',
              state: 'AZ',
              zip: ''
            },
            coordinates: {
              longitude: parseFloat(property.longitude || property.coordinates?.lng || -111.9261),
              latitude: parseFloat(property.latitude || property.coordinates?.lat || 33.4942)
            },
            images: property.images || [],
            thumbnails: property.thumbnails || [],
            daysOnMarket: property.daysOnMarket || 0,
            listingAgent: property.listingAgent || null,
            listingOffice: property.listingOffice || property.listingAgent?.officeName || null,
            propertyType: property.propertyType,
            yearBuilt: property.yearBuilt,
            description: property.description,
            features: property.features || []
          };
        }).filter((prop: any) => 
          prop.coordinates.longitude && 
          prop.coordinates.latitude &&
          prop.coordinates.longitude !== 0 &&
          prop.coordinates.latitude !== 0 &&
          !isNaN(prop.coordinates.longitude) &&
          !isNaN(prop.coordinates.latitude) &&
          Math.abs(prop.coordinates.longitude) <= 180 &&
          Math.abs(prop.coordinates.latitude) <= 90
        );
        
        console.log(`📍 Loaded ${transformedProperties.length} properties with valid coordinates`);
        setProperties(transformedProperties);
        
        // Load saved properties from the backend
        await loadSavedProperties();
      }
    } catch (error) {
      console.error('Failed to load properties:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('customerPortalToken');
    localStorage.removeItem('auth_token'); // Also remove public portal token
    navigate('/sign-in');
  };

  const fetchPendingDocuments = async () => {
    try {
      const authToken = localStorage.getItem('customerPortalToken');
      if (!authToken) return;
      
      const response = await axiosInstance.get('/customer-portal/documents/count', {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      
      if (response.data.success) {
        setPendingDocuments(response.data.pendingCount || 0);
      }
    } catch (error) {
      console.error('Error fetching document count:', error);
    }
  };

  // City coordinates for Arizona cities
  const cityCoordinates: { [key: string]: { lat: number; lng: number } } = {
    'gilbert': { lat: 33.3528, lng: -111.7890 },
    'scottsdale': { lat: 33.4942, lng: -111.9261 },
    'phoenix': { lat: 33.4484, lng: -112.0740 },
    'tempe': { lat: 33.4255, lng: -111.9400 },
    'mesa': { lat: 33.4152, lng: -111.8315 },
    'chandler': { lat: 33.3062, lng: -111.8413 },
    'glendale': { lat: 33.5387, lng: -112.1860 },
    'peoria': { lat: 33.5806, lng: -112.2374 },
    'surprise': { lat: 33.6292, lng: -112.3679 },
    'avondale': { lat: 33.4356, lng: -112.3496 },
    'goodyear': { lat: 33.4356, lng: -112.3576 },
    'queen creek': { lat: 33.2487, lng: -111.6341 },
    'fountain hills': { lat: 33.6114, lng: -111.7351 },
    'paradise valley': { lat: 33.5297, lng: -111.9738 }
  };

  // ZIP code coordinates for common Arizona ZIP codes
  const zipCoordinates: { [key: string]: { lat: number; lng: number } } = {
    // Gilbert ZIPs - Accurate center coordinates for each zip code
    '85233': { lat: 33.3528, lng: -111.7890 }, // Central Gilbert
    '85234': { lat: 33.3786, lng: -111.7580 }, // North Gilbert near Lindsay/Baseline
    '85236': { lat: 33.2792, lng: -111.7541 }, // Southeast Gilbert near Hunt Highway
    '85295': { lat: 33.3103, lng: -111.7860 }, // Southwest Gilbert near Cooper/Ray
    '85296': { lat: 33.3331, lng: -111.8397 }, // West Gilbert near McQueen/Elliott
    '85297': { lat: 33.2468, lng: -111.6420 }, // Far Southeast Gilbert/Queen Creek border
    '85298': { lat: 33.2930, lng: -111.6872 }, // Power Ranch area - Power Rd & Pecos
    // Scottsdale ZIPs
    '85250': { lat: 33.4942, lng: -111.9261 },
    '85251': { lat: 33.4942, lng: -111.9261 },
    '85252': { lat: 33.4942, lng: -111.9261 },
    '85254': { lat: 33.6114, lng: -111.8883 },
    '85255': { lat: 33.6997, lng: -111.8653 },
    '85258': { lat: 33.4942, lng: -111.9261 },
    '85259': { lat: 33.6997, lng: -111.8653 },
    '85260': { lat: 33.6334, lng: -111.9252 },
    // Phoenix ZIPs
    '85001': { lat: 33.4484, lng: -112.0740 },
    '85003': { lat: 33.4484, lng: -112.0740 },
    '85004': { lat: 33.4484, lng: -112.0740 },
    '85006': { lat: 33.4699, lng: -112.0555 },
    '85007': { lat: 33.4369, lng: -112.0874 },
    '85008': { lat: 33.4613, lng: -112.0813 },
    '85009': { lat: 33.4517, lng: -112.1235 },
    '85012': { lat: 33.5082, lng: -112.0704 },
    '85013': { lat: 33.5137, lng: -112.0840 },
    '85014': { lat: 33.5096, lng: -112.0569 },
    '85015': { lat: 33.5030, lng: -112.1040 },
    '85016': { lat: 33.5164, lng: -112.0298 },
    '85017': { lat: 33.5192, lng: -112.1213 },
    '85018': { lat: 33.5109, lng: -111.9865 },
    '85019': { lat: 33.5109, lng: -112.1426 },
    '85020': { lat: 33.5643, lng: -112.0461 },
    '85021': { lat: 33.5615, lng: -112.0923 },
    '85022': { lat: 33.6117, lng: -112.0353 },
    '85023': { lat: 33.6324, lng: -112.1104 },
    '85024': { lat: 33.6662, lng: -112.0353 },
    '85027': { lat: 33.6689, lng: -112.1213 },
    '85028': { lat: 33.5780, lng: -111.9811 },
    '85029': { lat: 33.5973, lng: -112.1159 },
    '85031': { lat: 33.4930, lng: -112.1319 },
    '85032': { lat: 33.6283, lng: -111.9811 },
    '85033': { lat: 33.4846, lng: -112.1852 },
    '85034': { lat: 33.4370, lng: -112.0095 },
    '85035': { lat: 33.4735, lng: -112.2279 },
    '85037': { lat: 33.4859, lng: -112.2766 },
    '85040': { lat: 33.3973, lng: -112.0014 },
    '85041': { lat: 33.3875, lng: -112.0853 },
    '85042': { lat: 33.3764, lng: -112.0230 },
    '85043': { lat: 33.4747, lng: -112.1532 },
    '85050': { lat: 33.6851, lng: -112.0461 },
    '85051': { lat: 33.5615, lng: -112.1532 },
    '85053': { lat: 33.6310, lng: -112.1749 },
    '85054': { lat: 33.6662, lng: -111.9353 },
    '85083': { lat: 33.7028, lng: -112.1048 },
    '85085': { lat: 33.7332, lng: -112.0353 },
    // Tempe ZIPs
    '85281': { lat: 33.4255, lng: -111.9400 },
    '85282': { lat: 33.4147, lng: -111.9089 },
    '85283': { lat: 33.3598, lng: -111.9269 },
    '85284': { lat: 33.3815, lng: -111.9658 },
    // Mesa ZIPs
    '85201': { lat: 33.4223, lng: -111.8226 },
    '85202': { lat: 33.3872, lng: -111.8447 },
    '85203': { lat: 33.4370, lng: -111.8031 },
    '85204': { lat: 33.3992, lng: -111.7836 },
    '85205': { lat: 33.4623, lng: -111.7217 },
    '85206': { lat: 33.3903, lng: -111.6835 },
    '85207': { lat: 33.4481, lng: -111.6386 },
    '85208': { lat: 33.3576, lng: -111.6470 },
    '85209': { lat: 33.3797, lng: -111.6384 },
    '85210': { lat: 33.4178, lng: -111.8478 },
    '85212': { lat: 33.3529, lng: -111.5474 },
    '85213': { lat: 33.4331, lng: -111.7541 },
    '85215': { lat: 33.4458, lng: -111.5875 },
    // Chandler ZIPs
    '85224': { lat: 33.3026, lng: -111.9211 },
    '85225': { lat: 33.2701, lng: -111.8514 },
    '85226': { lat: 33.3217, lng: -111.9669 },
    '85248': { lat: 33.2465, lng: -111.7903 },
    '85249': { lat: 33.2258, lng: -111.6906 },
    '85286': { lat: 33.3026, lng: -111.8414 },
    // Glendale ZIPs
    '85301': { lat: 33.5387, lng: -112.1859 },
    '85302': { lat: 33.5717, lng: -112.1579 },
    '85303': { lat: 33.5277, lng: -112.1325 },
    '85304': { lat: 33.5873, lng: -112.1676 },
    '85305': { lat: 33.5089, lng: -112.2680 },
    '85306': { lat: 33.6024, lng: -112.2828 },
    '85307': { lat: 33.5025, lng: -112.2077 },
    '85308': { lat: 33.6642, lng: -112.1676 },
    '85310': { lat: 33.6459, lng: -112.3359 },
    // Peoria ZIPs
    '85345': { lat: 33.4939, lng: -112.2311 },
    '85381': { lat: 33.5812, lng: -112.2396 },
    '85382': { lat: 33.6334, lng: -112.2342 },
    '85383': { lat: 33.6875, lng: -112.2369 },
    // Surprise ZIPs
    '85374': { lat: 33.6417, lng: -112.3679 },
    '85378': { lat: 33.6139, lng: -112.4539 },
    '85379': { lat: 33.6625, lng: -112.3976 },
    '85387': { lat: 33.6764, lng: -112.4526 },
    '85388': { lat: 33.6297, lng: -112.3333 },
    // Goodyear/Avondale ZIPs
    '85323': { lat: 33.4353, lng: -112.3579 },
    '85338': { lat: 33.4642, lng: -112.3967 },
    '85392': { lat: 33.4353, lng: -112.3378 },
    '85395': { lat: 33.4942, lng: -112.3526 },
    // Queen Creek ZIPs
    '85140': { lat: 33.2087, lng: -111.5982 },
    '85142': { lat: 33.2517, lng: -111.6340 },
    '85143': { lat: 33.1778, lng: -111.5124 },
    // Cave Creek/Carefree ZIPs
    '85331': { lat: 33.8223, lng: -111.8514 },
    '85327': { lat: 33.7114, lng: -111.9337 },
    '85377': { lat: 33.7977, lng: -111.9776 },
    // Paradise Valley ZIPs
    '85253': { lat: 33.5434, lng: -111.9261 },
    // Fountain Hills ZIP
    '85268': { lat: 33.6039, lng: -111.7180 },
    // Anthem ZIP
    '85086': { lat: 33.8681, lng: -112.1467 },
    '85087': { lat: 33.8669, lng: -112.1109 },
    // Buckeye ZIPs
    '85326': { lat: 33.3703, lng: -112.5838 },
    '85396': { lat: 33.4378, lng: -112.5962 },
    // Litchfield Park ZIP
    '85340': { lat: 33.4934, lng: -112.3579 },
    // Maricopa ZIP
    '85138': { lat: 33.0581, lng: -112.0476 },
    '85139': { lat: 33.0383, lng: -111.9746 },
    // Apache Junction ZIPs
    '85119': { lat: 33.4151, lng: -111.5496 },
    '85120': { lat: 33.3984, lng: -111.4919 },
    // Gold Canyon ZIP
    '85118': { lat: 33.3526, lng: -111.4235 },
    // San Tan Valley ZIP
    '85147': { lat: 33.1837, lng: -111.5474 },
    // Laveen ZIP
    '85339': { lat: 33.3620, lng: -112.1690 },
    // Tolleson ZIP
    '85353': { lat: 33.4503, lng: -112.2549 },
    // El Mirage ZIP
    '85335': { lat: 33.6131, lng: -112.3246 },
    // Youngtown ZIP
    '85363': { lat: 33.5939, lng: -112.2988 },
    // Sun City ZIPs
    '85351': { lat: 33.5975, lng: -112.2715 },
    '85372': { lat: 33.6297, lng: -112.2715 },
    '85373': { lat: 33.6409, lng: -112.2071 },
    '85375': { lat: 33.6642, lng: -112.2919 },
    // Sun City West ZIP (already included above)
    // Ahwatukee ZIP
    '85044': { lat: 33.3320, lng: -111.9853 },
    '85045': { lat: 33.3058, lng: -112.0643 },
    '85048': { lat: 33.3058, lng: -111.9782 }
  };

  const centerMapOnCity = (city: string) => {
    const cityLower = city.toLowerCase();
    const coords = cityCoordinates[cityLower];
    if (coords) {
      // Store in sessionStorage for MapView to pick up
      sessionStorage.setItem('mapCenterRequest', JSON.stringify({
        center: [coords.lng, coords.lat],
        zoom: 12,
        city: city
      }));
    }
  };

  const centerMapOnZipCode = (zipCode: string) => {
    const coords = zipCoordinates[zipCode];
    if (coords) {
      // Store in sessionStorage for MapView to pick up
      sessionStorage.setItem('mapCenterRequest', JSON.stringify({
        center: [coords.lng, coords.lat],
        zoom: 13, // Slightly more zoomed for ZIP codes
        zipCode: zipCode
      }));
    }
  };

  const handleSearch = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    
    if (!searchQuery.trim()) return;
    
    console.log('🔍 Searching with Theta AI for:', searchQuery);
    
    // Use Theta AI for search
    await handleInitialQuery(searchQuery);
    
    // Collapse search bar and switch to map view
    setShowSearch(false);
    setCurrentView('map');
  };

  const handleSendAgentMessage = (message: string) => {
    // TODO: Send message to agent via API
    console.log('Sending message to agent:', message);
    // For now, just show success message
    alert(`Message sent to ${user?.agentName}: ${message}`);
  };

  // Load saved properties from backend
  const loadSavedProperties = async () => {
    try {
      const response = await axiosInstance.get('/saved-properties/saved');
      if (response.data.success) {
        const saved = response.data.data;
        // Extract the property data from saved properties
        const properties = saved.map((savedProp: any) => {
          // Parse property_data if it's a string
          let propertyData = savedProp.property_data;
          if (typeof propertyData === 'string') {
            try {
              propertyData = JSON.parse(propertyData);
            } catch (e) {
              console.error('Failed to parse property data:', e);
              return null;
            }
          }
          return {
            ...propertyData,
            savedAt: savedProp.saved_at,
            savedNotes: savedProp.notes
          };
        }).filter(Boolean);
        
        setSavedProperties(properties);
        
        // Create a set of saved MLS numbers for quick lookup
        const savedIds = new Set<string>(properties.map((prop: any) => String(prop.mlsNumber)));
        setSavedPropertyIds(savedIds);
      }
    } catch (error) {
      console.error('Failed to load saved properties:', error);
    }
  };

  // Save a property
  const handleSaveProperty = async (property: any) => {
    try {
      const response = await axiosInstance.post('/saved-properties/save', {
        mlsNumber: property.mlsNumber,
        propertyData: property,
        notes: ''
      });

      if (response.data.success) {
        toast.success('Property saved!');
        
        // Update saved properties list with the property data
        setSavedProperties(prev => [...prev, property]);
        setSavedPropertyIds(prev => new Set([...prev, property.mlsNumber]));
      }
    } catch (error) {
      console.error('Failed to save property:', error);
      toast.error('Failed to save property');
    }
  };

  // Unsave a property
  const handleUnsaveProperty = async (mlsNumber: string) => {
    try {
      const response = await axiosInstance.delete(`/saved-properties/unsave/${mlsNumber}`);
      
      if (response.data.success) {
        toast.success('Property removed from saved');
        
        // Update saved properties list
        setSavedProperties(prev => prev.filter(p => p.mlsNumber !== mlsNumber));
        setSavedPropertyIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(mlsNumber);
          return newSet;
        });
      }
    } catch (error) {
      console.error('Failed to unsave property:', error);
      toast.error('Failed to remove property');
    }
  };

  // Toggle save/unsave
  const toggleSaveProperty = async (property: any) => {
    if (savedPropertyIds.has(property.mlsNumber)) {
      await handleUnsaveProperty(property.mlsNumber);
    } else {
      await handleSaveProperty(property);
    }
  };

  // Fetch seller property valuation
  const fetchSellerPropertyValuation = async (address: string) => {
    try {
      console.log('🏡 Fetching valuation for seller property:', address);
      
      // Get property valuation
      const response = await axiosInstance.post('/api/enhanced-valuation/comprehensive', {
        address: address,
        includeComparables: false
      });
      
      if (response.data.success && response.data.valuation) {
        const valuation = response.data.valuation;
        const coords = response.data.coordinates;
        
        // Update seller property with valuation data
        setSellerProperty((prev: any) => ({
          ...prev,
          estimatedValue: valuation.estimated_value,
          valueRange: valuation.value_range,
          lastSoldPrice: valuation.last_sold_price,
          lastSoldDate: valuation.last_sold_date,
          coordinates: coords,
          confidence: valuation.confidence || 'medium'
        }));
        
        console.log('✅ Seller property valuation:', valuation);
      }
    } catch (error) {
      console.error('Failed to fetch seller property valuation:', error);
      // Still show the property even if valuation fails
    }
  };

  const handleInitialQuery = async (query: string) => {
    console.log('🔍 Processing initial query with Theta AI:', query);
    setSearchQuery(query);
    // Don't set loading if we're already loaded - this prevents map refresh
    if (!user) {
      setLoading(true);
    }
    
    try {
      // Use Theta AI to process the natural language query
      const response = await axiosInstance.post('/theta-ai/search', {
        query: query,
        userId: user?.id || 'guest',
        sessionId: sessionStorage.getItem('sessionId') || `session-${Date.now()}`
      });
      
      console.log('🤖 Theta AI full response:', response.data);
      console.log('🤖 Success flag:', response.data.success);
      console.log('🤖 Properties count:', response.data.properties?.length || 0);
      console.log('🤖 Criteria:', response.data.criteria);

      // Handle Theta AI response - check for success flag OR properties
      if (response.data.success || (response.data.properties && response.data.properties.length > 0)) {
        console.log('🤖 Theta AI results:', response.data);

        // Store search context for feedback tracking
        if (response.data.searchContext) {
          sessionStorage.setItem('lastSearchQuery', response.data.searchContext.query);
          sessionStorage.setItem('lastSearchCriteria', JSON.stringify(response.data.searchContext.criteria));
        }
        
        // Update properties with Theta AI results
        if (response.data.properties && response.data.properties.length > 0) {
          setProperties(response.data.properties);
          
          // Update search filters from parsed criteria
          if (response.data.criteria) {
            setSearchFilters(response.data.criteria);
            
            // Center the map on the search location
            if (response.data.criteria.zipCode) {
              centerMapOnZipCode(response.data.criteria.zipCode);
            } else if (response.data.criteria.city) {
              centerMapOnCity(response.data.criteria.city);
            }
            
            // Update URL with just the natural language query
            // We don't need to expose all the extracted parameters - Theta AI handles the translation
            const searchParams = new URLSearchParams();
            searchParams.set('q', query);
            
            // Update URL without reloading the page - keep it simple and clean
            const newUrl = `${window.location.pathname}?${searchParams.toString()}`;
            window.history.pushState({}, '', newUrl);
          }
          
          // Show AI response in console (could add a toast notification)
          if (response.data.aiResponse) {
            console.log('💬 Theta AI says:', response.data.aiResponse);
          }
          
          // If we have properties, show them on the map
          if (response.data.totalFound > 0) {
            setCurrentView('map');
          }
        } else {
          // No properties found, try broader search
          console.log('No properties found, broadening search...');
          await fallbackToStandardSearch(query);
        }
      } else {
        // Theta AI failed, fallback to standard search
        await fallbackToStandardSearch(query);
      }
    } catch (error) {
      console.error('Theta AI search error:', error);
      // Fallback to standard search
      await fallbackToStandardSearch(query);
    } finally {
      setLoading(false);
      setSearchLoading(false);
    }
  };

  const fallbackToStandardSearch = async (query: string) => {
    // Simple pattern matching fallback
    const lowerQuery = query.toLowerCase();
    const filters: any = {};
    const searchParams = new URLSearchParams();
    
    // Check for ZIP code first
    const zipMatch = query.match(/\b85\d{3}\b/);
    if (zipMatch) {
      const zipCode = zipMatch[0];
      console.log('🏠 ZIP code detected:', zipCode);
      filters.zip = zipCode;
      searchParams.set('zip', zipCode);
    } else {
      // Extract city
      const cities = ['scottsdale', 'phoenix', 'tempe', 'mesa', 'chandler', 'gilbert', 'glendale'];
      for (const city of cities) {
        if (lowerQuery.includes(city)) {
          filters.city = city.charAt(0).toUpperCase() + city.slice(1);
          searchParams.set('city', filters.city);
          break;
        }
      }
    }
    
    // Extract price
    const priceMatch = lowerQuery.match(/(under|below|less than|max)\s*\$?(\d+)k?/i);
    if (priceMatch) {
      const price = parseInt(priceMatch[2]) * (priceMatch[0].includes('k') ? 1000 : 1);
      filters.maxPrice = price;
      searchParams.set('maxPrice', price.toString());
    }
    
    // Extract bedrooms
    const bedroomMatch = lowerQuery.match(/(\d+)\s*(bed|bedroom)/i);
    if (bedroomMatch) {
      filters.bedrooms = parseInt(bedroomMatch[1]);
      searchParams.set('beds', filters.bedrooms.toString());
    }
    
    // Always include the original query
    searchParams.set('q', query);
    
    // Update URL without reloading
    const newUrl = `${window.location.pathname}?${searchParams.toString()}`;
    window.history.pushState({}, '', newUrl);
    
    // Update search filters and load properties
    setSearchFilters(filters);
    loadPropertiesWithFilters(filters);
  };
  
  const loadPropertiesWithFilters = async (filters: any) => {
    try {
      const authToken = localStorage.getItem('customerPortalToken');
      const headers: any = {
        'Content-Type': 'application/json'
      };
      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      }

      const queryParams = new URLSearchParams({
        ...filters,
        limit: '500',
        state: 'AZ'
      }).toString();

      const response = await axiosInstance.get(`/mls-portal/properties/map?${queryParams}`, {
        headers
      });
      
      if (response.data.success && response.data.data) {
        console.log('✅ Found', response.data.data.length, 'properties for initial query');
        setProperties(response.data.data);
        
        // Show notification
        const notification = document.createElement('div');
        notification.className = 'saved-search-notification';
        notification.innerHTML = `Found ${response.data.data.length} properties for your search`;
        document.body.appendChild(notification);
        
        setTimeout(() => {
          notification.remove();
        }, 3000);
      }
    } catch (error) {
      console.error('Error loading filtered properties:', error);
    }
  };

  const handleApplySavedSearch = async (criteria: any, searchName: string) => {
    console.log('🔍 Applying saved search:', searchName, criteria);
    
    // Update search filters with saved search criteria
    setSearchFilters(criteria);
    
    try {
      const authToken = localStorage.getItem('customerPortalToken');
      const headers: any = {
        'Content-Type': 'application/json'
      };
      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      }

      const queryParams = new URLSearchParams({
        ...criteria,
        limit: '500',
        state: 'AZ'
      }).toString();

      const response = await axiosInstance.get(`/mls-portal/properties/map?${queryParams}`, {
        headers
      });
      
      if (response.data.success && response.data.data) {
        console.log('✅ Found', response.data.data.length, 'properties for saved search');
        setProperties(response.data.data);
        
        // Show notification
        const notification = document.createElement('div');
        notification.className = 'saved-search-notification';
        notification.innerHTML = `Applied search: ${searchName} (${response.data.data.length} properties found)`;
        document.body.appendChild(notification);
        
        setTimeout(() => {
          notification.remove();
        }, 3000);
      }
    } catch (error) {
      console.error('Error applying saved search:', error);
    }
    
    // Switch to map view if not already there
    if (currentView !== 'map') {
      setCurrentView('map');
    }
    
    // Close the saved searches panel to show the carousel again
    setShowSavedSearchPanel(false);
  };

  const renderMainContent = () => {
    switch (currentView) {
      case 'map':
        return (
          <MapView 
            properties={properties}
            onPropertySelect={(property: any) => {
              console.log('Selected property:', property);
            }}
            onToggleSave={toggleSaveProperty}
            savedPropertyIds={savedPropertyIds}
            thetaInsights={{
              // AI-generated insights for properties
              ...properties.reduce((insights, prop) => {
                const insights_pool = [
                  "Great investment potential - 15% appreciation expected",
                  "Top-rated schools nearby (9/10 rating)",
                  "Price reduced! Below market by $25k",
                  "Perfect for families - quiet neighborhood",
                  "Walking distance to premium shopping",
                  "Energy efficient home with solar panels",
                  "Recently renovated with modern finishes",
                  "Large lot with expansion potential"
                ];
                insights[prop.mlsNumber] = insights_pool[Math.floor(Math.random() * insights_pool.length)];
                return insights;
              }, {})
            }}
            showCarousel={!showSavedSearchPanel}
            carouselTitle="Available Properties"
            sellerProperty={sellerProperty}
            showSellerOverlay={showSellerOverlay}
            agentPhoto={user?.agentPhoto}
            agentName={user?.agentName}
            initialSearchFilters={searchFilters}
          />
        );
        
      case 'saved':
        // Show saved properties on map with carousel
        if (savedProperties.length === 0) {
          return (
            <div className="d-flex flex-column align-items-center justify-content-center" style={{ height: 'calc(100vh - 70px)' }}>
              <h3>No Saved Properties</h3>
              <p className="text-muted">Properties you save will appear here</p>
              <Button 
                variant="primary" 
                onClick={() => setCurrentView('map')}
              >
                Browse Properties
              </Button>
            </div>
          );
        }
        return (
          <MapView 
            properties={savedProperties}
            onPropertySelect={(property: any) => {
              console.log('Selected saved property:', property);
            }}
            onToggleSave={toggleSaveProperty}
            savedPropertyIds={savedPropertyIds}
            thetaInsights={{}}
            showCarousel={true}
            carouselTitle="Saved Properties"
            agentPhoto={user?.agentPhoto}
            agentName={user?.agentName}
            initialSearchFilters={searchFilters}
          />
        );
        
      case 'tours':
        const tourProps = getTourProperties();
        
        // Auto-open Tours panel when navigating to tours view
        if (!showToursPanel && tourProps.length > 0) {
          setShowToursPanel(true);
        }
        
        return (
          <MapView 
            properties={tourProps}
            onPropertySelect={(property: any) => {
              console.log('Selected tour property:', property);
            }}
            thetaInsights={{}}
            showCarousel={true}
            carouselTitle={`Your Tour List (${tourProps.length} ${tourProps.length === 1 ? 'home' : 'homes'})`}
            agentPhoto={user?.agentPhoto}
            agentName={user?.agentName}
            initialSearchFilters={searchFilters}
            onToggleSave={toggleSaveProperty}
            savedPropertyIds={savedPropertyIds}
          />
        );
        
      case 'searches':
        return <SavedSearches user={user} />;
        
      case 'chat':
        return <CustomerPortalChat user={user} isAuthenticated={isAuthenticated} />;
        
      case 'activity':
        return <ActivityDashboard user={user} />;
        
      case 'preferences':
        return (
          <Preferences 
            user={user}
            onSave={(prefs) => console.log('Save preferences:', prefs)}
          />
        );
        
      case 'documents':
        return <CustomerDocuments />;
        
      default:
        return <div>Select a view</div>;
    }
  };

  if (loading) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
          <p className="mt-3">Loading your personalized home search...</p>
        </div>
      </Container>
    );
  }

  if (!user) {
    return null; // Will redirect to login
  }

  return (
    <div className="customer-portal">
      {/* Enhanced Top Navigation Bar */}
      <Navbar bg="dark" variant="dark" className="portal-navbar d-none d-md-block" expand="lg">
        <Container fluid className="d-flex align-items-center">
          <Navbar.Brand href="#" className="d-flex align-items-center">
            <img 
              src="/connect-realty-logo.png" 
              alt="KairosIQ AI Realty" 
              style={{ 
                height: '40px', 
                marginRight: '10px',
                backgroundColor: 'white',
                padding: '5px',
                borderRadius: '5px'
              }}
            />
          </Navbar.Brand>
          
          {/* Main Navigation */}
          <Nav className="mx-auto main-nav">
            <Nav.Link 
              className={currentView === 'map' ? 'active' : ''}
              onClick={() => setCurrentView('map')}
            >
              <FontAwesomeIcon icon={faMap} className="me-2" />
              Map Search
            </Nav.Link>
            
            <Nav.Link 
              className={currentView === 'chat' ? 'active' : ''}
              onClick={() => setCurrentView('chat')}
            >
              <FontAwesomeIcon icon={faComments} className="me-2" />
              Chat
            </Nav.Link>
            
            <Nav.Link 
              className={currentView === 'saved' ? 'active' : ''}
              onClick={() => setCurrentView('saved')}
            >
              <FontAwesomeIcon icon={faHeart} className="me-2" />
              Saved
              {savedProperties.length > 0 && (
                <Badge bg="danger" className="ms-2">{savedProperties.length}</Badge>
              )}
            </Nav.Link>

            <Nav.Link 
              className={currentView === 'tours' ? 'active' : ''}
              onClick={() => {
                setCurrentView('tours');
                setShowToursPanel(true);
              }}
            >
              <FontAwesomeIcon icon={faCalendarCheck} className="me-2" />
              Tours
              {tourCount > 0 && (
                <Badge bg="primary" className="ms-2">{tourCount}</Badge>
              )}
            </Nav.Link>

            {!user?.isPublicUser && (
              <Nav.Link 
                className={currentView === 'documents' ? 'active' : ''}
                onClick={() => setCurrentView('documents')}
              >
                <FontAwesomeIcon icon={faFileContract} className="me-2" />
                Documents
                {pendingDocuments > 0 && (
                  <Badge bg="danger" className="ms-2">{pendingDocuments}</Badge>
                )}
              </Nav.Link>
            )}

            <Nav.Link 
              className={showSavedSearchPanel ? 'active' : ''}
              onClick={() => setShowSavedSearchPanel(!showSavedSearchPanel)}
            >
              <FontAwesomeIcon icon={faChartLine} className="me-2" />
              Saved Searches
            </Nav.Link>
          </Nav>

          {/* Expandable Search - Always visible */}
          <div className="expandable-search ms-auto me-3">
            {!showSearch ? (
              <Button
                variant="outline-light"
                size="sm"
                onClick={() => setShowSearch(true)}
                className="search-toggle"
              >
                <FontAwesomeIcon icon={faSearch} />
              </Button>
            ) : (
              <form onSubmit={handleSearch} className="search-form">
                <input
                  type="text"
                  placeholder="City, ZIP, or Address..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onBlur={() => {
                    if (!searchQuery.trim()) {
                      setTimeout(() => setShowSearch(false), 200);
                    }
                  }}
                  autoFocus
                  className="search-input"
                />
                <Button type="submit" variant="primary" size="sm">
                  <FontAwesomeIcon icon={faSearch} />
                </Button>
                <Button
                  variant="link"
                  size="sm"
                  onClick={() => {
                    setShowSearch(false);
                    setSearchQuery('');
                  }}
                  className="close-search"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </Button>
              </form>
            )}
          </div>

          {/* User Menu */}
          <Nav className="ms-auto">
            <div className="agent-info me-3">
              <small className="text-light">Agent: {user.agentName}</small>
              <Button 
                variant="link" 
                className="text-info p-0 ms-2"
                onClick={() => setShowAgentMessage(true)}
                title="Send message to agent"
              >
                <FontAwesomeIcon icon={faEnvelope} />
              </Button>
            </div>
            <Dropdown>
              <Dropdown.Toggle variant="dark" id="dropdown-basic">
                <FontAwesomeIcon icon={faUser} /> {user.name}
              </Dropdown.Toggle>
              <Dropdown.Menu align="end">
                <Dropdown.Item onClick={() => setCurrentView('preferences')}>
                  <FontAwesomeIcon icon={faCog} /> Settings
                </Dropdown.Item>
                <Dropdown.Item onClick={() => setCurrentView('activity')}>
                  <FontAwesomeIcon icon={faChartLine} /> Activity
                </Dropdown.Item>
                <Dropdown.Divider />
                <Dropdown.Item onClick={handleLogout}>
                  <FontAwesomeIcon icon={faSignOutAlt} /> Sign Out
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </Nav>
        </Container>
      </Navbar>

      {/* Full Screen Main Content */}
      <div className="main-content-wrapper">
        {renderMainContent()}
        
        {/* Search Loading Overlay - doesn't unmount the map */}
        {searchLoading && (
          <div className="search-loading-overlay">
            <div className="search-loading-content">
              <div className="spinner-border text-primary" role="status">
                <span className="sr-only">Searching...</span>
              </div>
              <p className="mt-2">Searching properties...</p>
            </div>
          </div>
        )}
      </div>

      {/* Agent Message Modal */}
      <AgentMessageModal
        show={showAgentMessage}
        onHide={() => setShowAgentMessage(false)}
        agentName={user.agentName}
        onSend={handleSendAgentMessage}
        message={agentMessage}
        setMessage={setAgentMessage}
      />

      {/* Saved Searches Floating Panel */}
      <SavedSearchesPanel
        isOpen={showSavedSearchPanel}
        onClose={() => setShowSavedSearchPanel(false)}
        user={user}
        onApplySearch={handleApplySavedSearch}
      />
      
      {/* Tours Floating Panel */}
      <ToursPanel
        isOpen={showToursPanel}
        onClose={() => setShowToursPanel(false)}
        tourProperties={getTourProperties()}
        onRemoveFromTour={(mlsNumber) => {
          const saved = localStorage.getItem('tourProperties');
          if (saved) {
            const tourIds = JSON.parse(saved).filter((id: string) => id !== mlsNumber);
            localStorage.setItem('tourProperties', JSON.stringify(tourIds));
            window.dispatchEvent(new Event('tourUpdated'));
          }
        }}
        onScheduleTour={() => {
          localStorage.removeItem('tourProperties');
          window.dispatchEvent(new Event('tourUpdated'));
        }}
      />
      {/* Mobile Bottom Navigation */}
      <div className="mobile-bottom-nav d-md-none">
        <div className={`nav-item ${currentView === 'map' ? 'active' : ''}`} onClick={() => setCurrentView('map')}>
          <FontAwesomeIcon icon={faMap} />
          <span>Search</span>
        </div>
        <div className={`nav-item ${currentView === 'saved' ? 'active' : ''}`} onClick={() => setCurrentView('saved')}>
          <FontAwesomeIcon icon={faHeart} />
          <span>Saved</span>
          {savedProperties.length > 0 && (
            <Badge bg="danger" className="nav-badge">{savedProperties.length}</Badge>
          )}
        </div>
        <div className={`nav-item ${currentView === 'chat' ? 'active' : ''}`} onClick={() => setCurrentView('chat')}>
          <FontAwesomeIcon icon={faComments} />
          <span>Chat</span>
        </div>
        <div className={`nav-item ${currentView === 'tours' ? 'active' : ''}`} onClick={() => {
          setCurrentView('tours');
          setShowToursPanel(true);
        }}>
          <FontAwesomeIcon icon={faCalendarCheck} />
          <span>Tours</span>
          {tourCount > 0 && (
            <Badge bg="primary" className="nav-badge">{tourCount}</Badge>
          )}
        </div>
        <div className={`nav-item ${showSavedSearchPanel ? 'active' : ''}`} onClick={() => setShowSavedSearchPanel(!showSavedSearchPanel)}>
          <FontAwesomeIcon icon={faChartLine} />
          <span>More</span>
        </div>
      </div>

      {/* Mobile Top Header - Minimal */}
      <div className="mobile-top-header d-md-none">
        <img src="/connect-realty-logo.png" alt="KairosIQ AI Realty" height="30" />
        <div className="mobile-search-icon" onClick={() => setShowSearch(!showSearch)}>
          <FontAwesomeIcon icon={faSearch} />
        </div>
      </div>
    </div>
  );
};

export default CustomerPortal;